<template>
  <div>
    <div style="margin-bottom: 1rem;">
      <fob-entity-header
        type="Public.headline.person"
        style="margin-bottom: 0rem; font-weight: bold;"
      ></fob-entity-header>
      <fob-public-select-tag
        view="person"
        @reset-selected-orga="resetSelectedOrga"
      >
      </fob-public-select-tag>
    </div>
    <div class="fob-abc-list">
      <!-- PERSON SEARCH | Input field for fulltext search -->
      <div style="margin-bottom: 1rem;">
        <b-field grouped>
          <b-field style="margin: 0 1rem 0 0; min-width: 300px; display: block;">
            <b-input
              v-model="searchStr"
              style="width: 300px;"
              :placeholder="$gettext('Public.person.search')"
              icon="search"
              @input="onFullName"
            >
            </b-input>
          </b-field>
          <b-tooltip square position="is-top" :label="$gettext(`form.xbutton`)" style="min-width: 35px;">
            <button tabindex="-1" class="button contol is-aquamarine" @click="searchStr = ''">
              <b-icon icon="times" size="is-small"></b-icon>
            </button>
          </b-tooltip>
        </b-field>
        <div style="height: 20px;">
          <p v-if="staffTotalEntries > staff.length" class="help is-error is-italic">
            <translate :translate-params="{staffTotalEntries}">Public.person.search.limit</translate>
          </p>
          <p v-else-if="staffTotalEntries" class="help is-italic">
            <translate :translate-params="{staffTotalEntries}">Public.person.search.match</translate>
          </p>
          <p v-else class="help is-italic">
            <translate>Public.person.search.nomatch</translate>
          </p>
        </div>
      </div>
      <!-- PERSON SEARCH | ABC - Search -->
      <div class="columns is-multiline" style="position: relative;">
        <div
          class="column is-narrow"
          style="padding-bottom: 0px; width: calc(97%/7);"
          v-for="letter in alphabet"
          :key="letter"
        >
          <a
            class="button"
            @click="loadStaffStartsWith(letter)"
            style="width: 2.5rem; height: 2.5rem"
            :class="{ 'is-aquamarine': letter === staffStartsWith }"
            :disabled="!staffLetterCount[letter]"
            :title="getTitleForLetter(letter)"
          >
            {{letter}}
          </a>
        </div>
        <div class="column">
          <a
            class="button"
            :class="{ 'is-aquamarine': staffStartsWith === ''}"
            style="width: 2.5rem; height: 2.5rem; padding: 0rem;"
            @click="loadStaffStartsWith('')"
            :title="getTitleForLetter('AZ')"
          >
            A…Z
          </a>
        </div>
        <b-loading :is-full-page="false" :active.sync="isLoadingStaff"></b-loading>
      </div>
      <!-- PERSON SEARCH | List persons -->
      <div
        v-if="!isLoadingStaff && staff && staff.length > 0"
        class="fob-person-search-results"
        style="min-width: 450px; margin-top: 1rem;"
      >
        <hr class="fob-public-select-divider" align="left" />
        <ul>
          <router-link
            v-for="person in staff"
            :key="`person-${person.id}-${activeResourceType}`"
            class="control"
            style="padding: 2px 0 2px 0"
            :title="person.organisationseinheiten.map(x => `${x.titelDe}`).join('\n')"
            tag="li"
            :to="toPublicRoute('person', person.id, activeResourceType)"
          >
            <b-icon pack="fa" icon="user"></b-icon>
            <a>{{fullNameWithTitle(person)}}</a>
          </router-link>
        </ul>
        <!-- PERSON SEARCH | List persons | Fade out persons list, if too long -->
        <div class="fadeout" v-if="staffTotalEntries > staff.length">
          <span
            class="help is-italic has-text-centered"
            style="padding-top: 6em; width: 300px;"
            v-html="$gettext('Public.person.list.limit')"
          ></span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import { mapActions, mapGetters } from 'vuex'
import CompactViewHelper from '@/components/mixins/CompactViewHelper'
import debounce from 'debounce'
import EntityHeader from '@/components/EntityHeader'
import PublicSelectTag from '@/components/public/PublicSelectTag'
import PublicViewHelper from '@/components/mixins/PublicViewHelper'
import TooltipHelper from '@/components/mixins/TooltipHelper'

export default {
  name: 'fob-public-select-person',
  mixins: [CompactViewHelper, PublicViewHelper, TooltipHelper],
  data () {
    return {
      maxLengthOfTags: 46, // will also set by TooltipHelper - default: 30
      searchStr: ''
    }
  },
  computed: {
    ...mapGetters({
      isLoadingStaff: 'publicView/isLoadingStaff',
      selectedOrganisationalUnit: 'tree/selected',
      staff: 'publicView/getStaffStartsWithData',
      staffLetterCount: 'publicView/getStaffLetterCount',
      staffStartsWith: 'publicView/getStaffStartsWith',
      staffTotalEntries: 'publicView/getStaffTotalEntries'
    }),
    staffFullName: {
      get () {
        return this.$store.getters['publicView/getStaffFullName']
      },
      set (fullName) {
        return this.$store.dispatch('publicView/setStaffFullName', fullName)
      }
    },
    alphabet () {
      return [...Array(26)].map((a, idx) => {
        return String.fromCharCode(97 + idx).toUpperCase()
      })
    }
  },
  methods: {
    ...mapActions({
      resetSelectedTreeItem: 'tree/reset'
    }),
    getTitleForLetter (letter) {
      let count = 0
      if (letter === 'AZ') {
        for (let letter in this.staffLetterCount) {
          count += this.staffLetterCount[letter]
        }
      } else {
        count = this.staffLetterCount[letter]
      }
      return count > 0
        ? `${count} ${this.$gettext('Public.person')}`
        : ''
    },
    loadAsyncStaff ({ startsWith, filters }) {
      let options = {
        startsWith: this.staffLetterCount[startsWith] || startsWith === '' ? startsWith : this.staffStartsWith,
        filter: {
          organisationalUnit: this.selectedOrganisationalUnit.id,
          fullName: this.searchStr != null ? this.searchStr : this.staffFullName,
          ...filters
        }
      }
      this.$store.dispatch('publicView/loadAsyncStaff', options)
    },
    loadStaffStartsWith (startsWith) {
      if (this.staffLetterCount[startsWith] || startsWith === '') {
        this.loadAsyncStaff({ startsWith })
      }
    },
    onFullName: debounce(function () {
      this.loadAsyncStaff({})
    }, 500),
    resetSelectedOrga () {
      this.resetSelectedTreeItem()
      this.loadAsyncStaff({})
    }
  },
  beforeMount () {
    // clear input fullname search
    this.staffFullName = ''
  },
  mounted () {
    // reset all inputs on init and fetch persons
    this.loadAsyncStaff({
      startsWith: '',
      filters: {
        fullName: ''
      }
    })
  },
  components: {
    'fob-entity-header': EntityHeader,
    'fob-public-select-tag': PublicSelectTag
  }
}

</script>

<style lang="scss">
  .fob-abc-list {
    padding: 0 3rem 0 0;
    width: 400px;

    .box {
      height: 2.5rem !important;
      width: 2.5rem !important;
    }

    .fadeout {
      position: relative;
      bottom: 4em;
      height: 4em;
      background: -webkit-linear-gradient(
        rgba(255, 255, 255, 0) 0%,
        rgba(249, 249, 249, 1) 100%
      );
      background-image: -moz-linear-gradient(
        rgba(255, 255, 255, 0) 0%,
        rgba(249, 249, 249, 1) 100%
      );
      background-image: -o-linear-gradient(
        rgba(255, 255, 255, 0) 0%,
        rgba(249, 249, 249, 1) 100%
      );
      background-image: linear-gradient(
        rgba(255, 255, 255, 0) 0%,
        rgba(249, 249, 249, 1) 100%
      );
      background-image: -ms-linear-gradient(
        rgba(255, 255, 255, 0) 0%,
        rgba(249, 249, 249, 1) 100%
      );
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/public/PublicSelectPerson.vue